/**
 * @module app.shared
 */

import { bindingMode, autoinject, processContent, computedFrom, TaskQueue } from 'aurelia-framework';
import flatpickr from 'flatpickr';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { bindable } from 'aurelia-typed-observable-plugin';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { DateValue } from 'app/shared/lib/date-time';
import { updateMaterializeInputLabel } from 'app/shared/lib/materialize/dom-utilities';
import { guid } from 'app/shared/lib/guid';
import { getKey, Key } from 'app/shared/lib/keys';
import { microTask, defer } from 'app/shared/lib/decorators';
import { moveRulesToHelperSlot } from 'app/shared/elements/ess-rule';
import { IInput } from 'app/shared/elements/forms/input-base';
import { stop } from 'app/shared/lib/noop';
import { parse } from 'date-fns';
import { deferEvent } from 'app/shared/lib/fire-events';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { I18N } from 'aurelia-i18n';
import { focusNext, focusPrevious } from 'app/shared/lib/focus';

const SQL_DATETIME_MIN_DATE = '1753-01-01';
@autoinject()
@processContent(moveRulesToHelperSlot)
export class EssDateCustomElement implements IInput {

    protected id: guid = guid();

    @bindable.stringNull({ defaultBindingMode: bindingMode.twoWay })
    public value: string | null = null;

    @bindable.string()
    public testName: string = '';

    @bindable.stringNull({ changeHandler: 'minDateChanged' })
    public minDate: string = SQL_DATETIME_MIN_DATE;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public maxDate: string | null = null;

    @bindable.stringNull()
    public label: string | null = null;

    @bindable.stringNull()
    public requiredValidatorLabel: string | null = null;

    @bindable.stringNull()
    public placeholder: string | null = null;

    @bindable.string()
    public autocomplete: string = 'off';

    @bindable.booleanAttr()
    public required: boolean = false;

    @bindable.booleanAttr()
    public readonly: boolean = false;

    @bindable.booleanAttr()
    public disabled: boolean = false;

    @bindable()
    protected labelledbyId: string | null = null;  //An element's `id`. Sets the component's `aria-labelledby

    protected instance: flatpickr.Instance | null = null;
    protected isChanging: boolean = false;
    protected pickerId: string | null = null;
    protected onCloseFollowup: Function | null = null;

    // refs
    protected readonly input!: HTMLInputElement;
    protected readonly element!: HTMLElement;
    protected readonly labelElement!: HTMLLabelElement;

    protected deferEvent = deferEvent;

    constructor(
        protected readonly formatter: FormatProvider,
        private readonly i18n: I18N,
        protected readonly env: EnvironmentService,
        protected readonly logger: AppLogger,
        protected queue: TaskQueue) { }

    protected attached(): void {
        this.initializePicker();
        this.placeholderChanged();
    }

    protected detached(): void {
        this.destroyPicker();
    }

    protected destroyPicker() {
        if(this.instance) {
            if(this.instance.calendarContainer) {
                this.instance.calendarContainer.removeEventListener('keydown', this.handlePickerKeydown, { capture: true });
            }
            this.instance.destroy();
            this.instance = null;
        }
    }

    protected initializePicker() {

        // INFO [Matt] Called by the change handlers before element is attached,
        // so we need to check that the element is attached.
        if(!this.element) {
            return;
        }

        const minDate = parse(this.minDate || '', 'yyyy-MM-dd', 0);
        const maxDate = parse(this.maxDate || '', 'yyyy-MM-dd', 0);
        const onChange = this.onChange.bind(this);
        const onClose = this.onClose.bind(this);
        const onOpen = this.onOpen.bind(this);
        const formatDate = this.formatDate.bind(this);

        this.destroyPicker();
        this.instance = flatpickr(this.element, {
            allowInput: !this.readonly && !this.disabled,
            clickOpens: false,
            minDate: isNaN(Number(minDate)) ? undefined : minDate,
            maxDate: isNaN(Number(maxDate)) ? undefined : maxDate,
            wrap: true,
            onOpen,
            onChange,
            onClose: [onChange, onClose],
            disableMobile: true,
            ariaDateFormat: 'aria',
            parseDate: (s: string, f: string) => new Date(s),
            formatDate,
            onReady: (_selectedDates, _dateStr, instance) => {
                // Prevent the date picker from opening when focused on and the enter key is pressed
                if(this.readonly || this.disabled) {
                    const input = (instance.altInput ?? instance.input) as HTMLInputElement;

                    input.addEventListener(
                        "keydown",
                        (e: KeyboardEvent) => {
                            if(e.key === "Enter") {
                                e.preventDefault();
                                e.stopImmediatePropagation();
                            }
                        },
                        true // capture phase: runs before Flatpickr's internal listener
                    );
                }
            }
        });

        this.instance.input.readOnly = true;

        if(this.instance && this.instance.calendarContainer) {
            this.pickerId = this.instance.calendarContainer.getAttribute('id');
            if(!this.pickerId) {
                this.pickerId = `${this.id}-picker`;
                this.instance.calendarContainer.setAttribute('id', this.pickerId);
            }

            // Attach a listener to control when a user tabs out of the container
            this.instance.calendarContainer.addEventListener('keydown', this.handlePickerKeydown.bind(this), { capture: true });
        }

        this.updateValue();
    }

    protected valueChanged(newValue: string | null, oldValue: string | null): void {
        if(this.instance && (newValue !== oldValue)) {
            this.updateValue();
        }

        if(this.element) {
            var event = new CustomEvent('value-changed', {
                detail: newValue,
                bubbles: true
            });

            this.element.dispatchEvent(event);
        }
    }

    protected handleFocusOut(event: FocusEvent): true {

        // INFO [Matt] Essentially copying the the internal logic of flatpickr on
        // enter key press to the case where focus is lost, e.g. tabs. See
        // https://github.com/flatpickr/flatpickr/blob/v4.6.2/src/index.ts#L1593.
        if(this.instance && !this.instance.calendarContainer.contains(event.relatedTarget as Node)) {
            this.instance.setDate(
                this.instance._input.value,
                true,
                this.instance.config.dateFormat
            );
        }
        return true;
    }

    @computedFrom('label', 'requiredValidatorLabel')
    protected get requiredValueMessage() {
        return this.requiredValidatorLabel || this.i18n.tr('ui:validation:required', { what: this.i18n.tr(this.label || 'ui:labels.value') });
    }

    protected updateValue(newValue?: string): void {
        if(!this.isChanging && this.instance) {
            const dateValue = new DateValue(newValue || this.value || '');
            if(dateValue.isValid()) {
                const parts = dateValue.toParts();
                const nativeDate = new Date(parts.year, parts.month - 1, parts.date);
                this.instance.setDate(nativeDate, false); // don't fire change
            }
            else {
                this.instance.clear(false); // don't fire change
            }
            this.queueUpdateLabel();
        }
    }

    protected open(): void {
        if(this.instance && !this.readonly) {
            this.instance.open();
        }
    }

    protected close(): void {
        if(this.instance) {
            this.instance.close();
        }
    }

    protected toggle(): void {
        if(this.instance && !this.readonly) {
            if(this.instance.isOpen) {
                this.onCloseFollowup = () => {
                    // Transition focus back to the input, when a UIEvent on the input causes the close
                    this.input.focus({ preventScroll: true });
                }
            }
            this.instance.toggle();
        }
    }

    protected clearValue(): void {
        if(this.readonly) { return; }
        if(this.instance) {
            this.instance.clear(false); // don't fire change
            this.instance.close();
        }
        this.value = null;
    }

    protected handleKeyDown(event: KeyboardEvent): boolean {
        if(this.readonly) return false;
        // improving the lives of keyboard users
        const key = getKey(event);
        switch(key) {
            case Key.Down:
            case Key.Enter:
            case Key.Space:
                event.stopPropagation();  // prevent flatpickr's handling of "enter" on the input (as well as default focusing behavior)
                // ensure calendar is updated after manual entry occurs
                if(this.instance) {
                    const dateValue = this.getDateValue(new Date(this.instance._input.value)).toJSON();
                    if(dateValue) this.queue.queueTask(() => this.updateValue(dateValue));
                    else this.queue.queueTask(() => this.clearValue());
                }
                this.queue.queueTask(() => this.open());
                return false;
            case Key.Delete:
                this.clearValue();
                return false;
            default:
                return true;
        }
    }

    protected handleInputMouseEvent(event: MouseEvent): boolean {
        event.stopPropagation();  // prevent flatpickr's handling of click or mousedown on the input
        this.toggle();
        return false;
    }

    protected handlePickerKeydown(event: KeyboardEvent): boolean {
        const key = getKey(event) as Key;
        // INFO [MW] Control tab key events on the flatpickr calender container, which has no tabable elements.
        // WARNING: The time picker portion of flatpickr does have tabable elements.
        if(key === Key.Tab) {
            event.preventDefault();
            event.stopPropagation();
            this.close();

            if(event.shiftKey) {
                focusPrevious(this.input);
            }
            else {
                focusNext(this.input);
            }
            return true;
        }
        return true;  // turn off Aurelia's automatic preventDefault() on events handled with delegate or trigger binding.
    }

    protected onChange(selectedDates: Date[]): void {
        if(!selectedDates.length) {
            this.synchronizeValue(null);
        }
        else {
            const date = selectedDates[0];
            const value = this.getDateValue(date).toJSON();
            this.synchronizeValue(value);
        }
        this.queueUpdateLabel();
    }

    @microTask
    protected synchronizeValue(value: string | null): void {
        // bindable values update asynchronously, so run this in a microtask
        try {
            // prevent infinite recursion
            this.isChanging = true;
            this.value = value;
        }
        finally {
            this.isChanging = false;
        }
    }

    protected placeholderChanged(): void {
        this.queueUpdateLabel();
    }

    @defer
    protected queueUpdateLabel(): void {
        if(this.input && this.labelElement) {
            updateMaterializeInputLabel(this.input, this.labelElement);
        }
    }

    protected stop(event: Event) {
        if(this.readonly) {
            stop(event);
        }
        return true
    };

    protected onOpen(): void {
        this.input.classList.add('ess-open');
        this.input.setAttribute('aria-expanded', 'true');

        if(this.instance) {
            // move label out of way when popup opens
            if(!this.value && !this.placeholder) {
                this.input.setAttribute('placeholder', ' ');
            }

            // set focus to the calendar (selected day and current day elements are prioritized)
            const days = this.instance.days;
            if(days) {
                // TODO [Matt] Need to cast as an any to allow building on the build server.
                const selectedDay = <any>days.querySelector('.flatpickr-day.selected:not(.flatpickr-disabled)');
                const today = <any>days.querySelector('.flatpickr-day.today:not(.flatpickr-disabled)');
                const firstAvailableDay = <any>days.querySelector('.flatpickr-day:not(.flatpickr-disabled)');
                if(selectedDay) {
                    selectedDay.focus({ preventScroll: true });
                } else if(today) {
                    today.focus({ preventScroll: true });
                } else if(firstAvailableDay) {
                    firstAvailableDay.focus({ preventScroll: true });
                } else if(this.instance.calendarContainer) {
                    (<any>this.instance.calendarContainer).focus({ preventScroll: true });
                }
            }
        }
    }

    protected onClose(): void {
        this.input.classList.remove('ess-open');
        this.input.setAttribute('aria-expanded', 'false');

        if(!this.placeholder) {
            // return label to original place
            this.input.setAttribute('placeholder', '');
        }

        if(this.onCloseFollowup) {
            this.onCloseFollowup();
            this.onCloseFollowup = null;
        }
    }

    protected formatDate(date: Date, format: string): string {
        const value = this.getDateValue(date);
        const options: any = {};
        if(format === 'aria') {
            options.format = 'aria';
        }
        return this.formatter.formatDate(value, options) || '';
    }

    protected getDateValue(date: Date): DateValue {
        return DateValue.fromParts({
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            date: date.getDate(),
        });
    }

    // INFO If this bindable is changed somewhere, the app should fire this
    // this type of warning can be useful in tracing back issues.
    protected async minDateChanged(newValue: string) {
        if(newValue < SQL_DATETIME_MIN_DATE) {
            this.logger.warn('Bindable property [min-date] was set below the application minimum');
        }
        this.initializePicker();
    }

}
